import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import Tabs from "../components/Tabs";
import BillingOverviewTab from "../components/billing/BillingOverviewTab";
import BillingPaymentsTab from "../components/billing/BillingPaymentsTab";
import BillingContractsTab from "../components/billing/BillingContractsTab";
import BillingPaymentTypesTab from "../components/billing/BillingPaymentTypesTab";
import BillingMonthEndTab from "../components/billing/BillingMonthEndTab";
import BillingFirstContractCard from "../components/billing/BillingFirstContractCard";
import { useAppDispatch } from "../app/hooks";
import { fetchContracts } from "../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../features/payment-types/slice-extensions/payment-types-slice-extension";

const TAB_KEYS = ["overview", "month-end", "payments", "contracts", "payment-types"] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * The billing page: the attention overview plus management tabs for Payments
 * (pack purchases, drop-ins, status lifecycle), Contracts (packs and
 * subscriptions) and the Payment-type price catalog. A truly fresh account
 * (no contracts, no prices — so no payments either) gets the first-contract
 * step instead of five empty tabs.
 */
const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  // null = still probing; true = show the first-contract step instead of tabs.
  const [firstRun, setFirstRun] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      dispatch(fetchContracts({ page: 1, pageSize: 1 })).unwrap(),
      dispatch(fetchPaymentTypes({ includeArchived: true })).unwrap(),
    ])
      .then(([contracts, paymentTypes]) => {
        if (active) setFirstRun(contracts.totalCount === 0 && paymentTypes.length === 0);
      })
      .catch(() => {
        // Probing failed — fall back to the normal tabs.
        if (active) setFirstRun(false);
      });
    return () => {
      active = false;
    };
  }, [dispatch]);

  const tabLabels = [
    t("billing.tabs.overview"),
    t("billing.tabs.monthEnd"),
    t("billing.tabs.payments"),
    t("billing.tabs.contracts"),
    t("billing.tabs.paymentTypes"),
  ];

  const tabParam = searchParams.get("tab") as TabKey | null;
  const selectedIndex = Math.max(0, TAB_KEYS.indexOf(tabParam ?? "overview"));

  const selectTab = (index: number) => {
    const params = new URLSearchParams();
    if (index > 0) params.set("tab", TAB_KEYS[index]);
    setSearchParams(params);
  };

  return (
    <div className="flex">
      <LeftMenu />
      <div className="min-w-0 flex-1 p-6">
        <Breadcrumbs />
        <h1 className="mt-4">{t("billing.title")}</h1>

        {firstRun === true && (
          <div className="mt-6 max-w-2xl">
            <BillingFirstContractCard onCreated={() => setFirstRun(false)} />
          </div>
        )}

        {firstRun === false && (
          <>
            <div className="mt-4">
              <Tabs
                tabs={tabLabels}
                selectedIndex={selectedIndex}
                onSelect={selectTab}
                headerClassName="tabs-header-fit"
                idPrefix="billing-tab"
              />
            </div>

            {selectedIndex === 0 && <BillingOverviewTab />}
            {selectedIndex === 1 && <BillingMonthEndTab />}
            {selectedIndex === 2 && <BillingPaymentsTab />}
            {selectedIndex === 3 && <BillingContractsTab />}
            {selectedIndex === 4 && <BillingPaymentTypesTab />}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
